<mat-toolbar color="primary" class="toolbar">
    <img [src]="IMAGE_LINQ" alt="LINQ" width="80" height="55">
    <span class="spacer"></span>
    @if (choosing) {
        <!-- the choices include looking round the demo, which is where "Not now" would go -->
    }@else if (adding) {
        <button mat-button (click)="notNow()" [disabled]="creating || building" [matTooltip]="existingModel ? 'Back to your model, still empty' : created ? 'Leave. Your model is kept, empty, to build later' : 'Leave without creating a model'">Cancel</button>
    }@else {
        <button mat-button (click)="notNow()" [disabled]="creating || building" [matTooltip]="organisation || created ? 'Leave getting started. You can create a model at any time from the home page' : 'Leave getting started, back to the ways to start'">Not now</button>
    }
    <!-- the page has no app menu, so who is signed in, and signing out, are here -->
    @if (user) {
        <button mat-icon-button [matMenuTriggerFor]="accountMenu" [disabled]="creating || building" matTooltip="Your account" aria-label="Your account" data-testid="get-started-account">
            <mat-icon>account_circle</mat-icon>
        </button>
        <mat-menu #accountMenu="matMenu" xPosition="before">
            <div class="account-who" (click)="$event.stopPropagation()">
                <b class="truncated-name" [title]="user.name">{{user.name}}</b>
                <span class="truncated-name" [title]="user.email">{{user.email}}</span>
            </div>
            <mat-divider></mat-divider>
            <button mat-menu-item (click)="signOut()" data-testid="get-started-sign-out"><mat-icon>exit_to_app</mat-icon><span>Sign out</span></button>
        </mat-menu>
    }
</mat-toolbar>

<main class="get-started" [class.choosing]="choosing">
    <header>
        <h1>{{choosing ? 'Welcome to LINQ' : existingModel ? 'Build your model with AI' : adding ? 'Create a model with AI' : 'Create your first model'}}</h1>
        @if (choosing) {
            <p class="lead">LINQ models how your business works, so you can see where time, cost and value go and what a change would do. See it at work on an example organisation, learn the basics by building a small model, or go straight to modelling your own.</p>
        }@else if (existingModel) {
            <p class="lead">Tell AI about the process and it will build <b>{{existingModel.name}}</b>: the steps, the people and systems involved, and where the time and cost goes. You can change anything afterwards.</p>
        }@else if (adding) {
            <p class="lead">Tell AI about the process and it will build a model of it: the steps, the people and systems involved, and where the time and cost goes. You can change anything afterwards.@if (project) { It will be in the project <b>{{project.name}}</b>.}</p>
        }@else {
            <p class="lead">Tell us about a process in your business and AI will build a model of it: the steps, the people and systems involved, and where the time and cost goes. It takes a few minutes, and you can change anything afterwards.</p>
        }
    </header>

    @if (loading) {
        <mat-progress-bar mode="indeterminate" aria-label="Loading"></mat-progress-bar>
    }@else if (choosing) {
        <div class="start-choices" role="group" aria-label="How to start">
            <button type="button" class="start-choice" (click)="walkThroughDemo()" data-testid="start-demo-walkthrough">
                <mat-icon aria-hidden="true">tour</mat-icon>
                <h2>Walk through the demo organisation</h2>
                <p>See how an example business is modelled: step through one of its processes, then see it simulated, as a heatmap and as an economic report.</p>
                <span class="start-cta">Start the walkthrough<mat-icon aria-hidden="true">arrow_forward</mat-icon></span>
            </button>
            <button type="button" class="start-choice" (click)="openDemoOrganisation()" data-testid="start-open-demo">
                <mat-icon aria-hidden="true">travel_explore</mat-icon>
                <h2>Explore the demo organisation</h2>
                <p>Look round the example business at your own pace: its models, the people and systems that do the work, and its insights.</p>
                <span class="start-cta">Open the demo<mat-icon aria-hidden="true">arrow_forward</mat-icon></span>
            </button>
            <button type="button" class="start-choice" (click)="learnTheBasics()" data-testid="start-learn-basics">
                <mat-icon aria-hidden="true">school</mat-icon>
                <h2>Learn the basics of modelling</h2>
                <p>Build a small model yourself, step by step: drag the steps onto the canvas, connect them, and add the people and systems. It takes a few minutes.</p>
                <span class="start-cta">Start learning<mat-icon aria-hidden="true">arrow_forward</mat-icon></span>
            </button>
            <button type="button" class="start-choice" (click)="createOwnOrganisation()" data-testid="start-create-organisation">
                <mat-icon aria-hidden="true">add_business</mat-icon>
                <h2>Create your own organisation</h2>
                <p>Tell us about a process in your business and AI will build a model of it. It takes a few minutes, and you can change anything afterwards.</p>
                <span class="start-cta">Get started<mat-icon aria-hidden="true">arrow_forward</mat-icon></span>
            </button>
        </div>
    }@else {
        <mat-stepper linear orientation="vertical" #stepper class="steps">

            <!-- 1. organisation -->
            <mat-step [stepControl]="organisationForm" [editable]="!created" [completed]="adding && organisation?.industry ? true : null" label="Your organisation">
                <form [formGroup]="organisationForm" class="step">
                    <p class="intro">
                        @if (organisation && project) {
                            Your model will be created in the project <b>{{project.name}}</b> of <b>{{organisation.name}}</b>.
                        }@else if (organisation) {
                            Your model will be created in <b>{{organisation.name}}</b>.
                        }@else {
                            Your organisation holds your models and the people you work with.
                        }
                    </p>
                    @if (!organisation) {
                        <mat-form-field appearance="fill">
                            <mat-label>Organisation name</mat-label>
                            <input matInput formControlName="name" autocomplete="organization" [maxlength]="ORGANISATION_NAME_MAX" required>
                            @if (organisationForm.controls.name.invalid) {
                                <mat-error>Organisation name must be between {{ORGANISATION_NAME_MIN}} and {{ORGANISATION_NAME_MAX}} characters</mat-error>
                            }
                        </mat-form-field>
                    }
                    <mat-form-field appearance="fill">
                        <mat-label>Industry</mat-label>
                        <mat-select formControlName="industry" required>
                            @for (industry of industries; track industry) {
                                <mat-option [value]="industry">{{industry}}</mat-option>
                            }
                        </mat-select>
                        <mat-hint>AI uses this to suggest what's typical for businesses like yours</mat-hint>
                        @if (organisationForm.controls.industry.invalid) {
                            <mat-error>Choose the closest industry</mat-error>
                        }
                    </mat-form-field>
                    <div class="actions">
                        <button mat-flat-button color="accent" matStepperNext type="button">Next</button>
                    </div>
                </form>
            </mat-step>

            <!-- 2. process -->
            <mat-step [stepControl]="processForm" [editable]="!created" [completed]="existingModel ? true : null" label="Choose a process">
                <form [formGroup]="processForm" class="step" (ngSubmit)="createModel()">
                    <p class="intro">Pick one process to start with. Something you know well, and that people spend time on, works best.</p>

                    <span class="label" id="function-label">Business area</span>
                    <mat-chip-listbox formControlName="function" aria-labelledby="function-label" class="functions">
                        @for (f of functions; track f) {
                            <mat-chip-option [value]="f">{{f}}</mat-chip-option>
                        }
                    </mat-chip-listbox>
                    @if (processForm.controls.function.invalid && processForm.controls.function.touched) {
                        <p class="error" role="alert">Choose the business area the process belongs to</p>
                    }

                    <mat-form-field appearance="fill">
                        <mat-label>In a sentence, what does the process do?</mat-label>
                        <input matInput formControlName="description" [maxlength]="MODEL_DESCRIPTION_MAX" required placeholder="e.g. Taking a customer order through to delivery">
                        @if (processForm.controls.description.invalid) {
                            <mat-error>Describe the process in a sentence</mat-error>
                        }
                    </mat-form-field>

                    <mat-form-field appearance="fill">
                        <mat-label>Model name</mat-label>
                        <input matInput formControlName="name" [maxlength]="MODEL_NAME_MAX" required>
                        @if (processForm.controls.name.invalid) {
                            <mat-error>Model name must be between {{MODEL_NAME_MIN}} and {{MODEL_NAME_MAX}} characters</mat-error>
                        }
                    </mat-form-field>

                    @if (createError) {
                        <p class="error" role="alert">{{createError}}</p>
                    }
                    <div class="actions">
                        <button mat-button matStepperPrevious type="button">Back</button>
                        <button mat-flat-button color="accent" type="submit" [disabled]="creating">{{creating ? 'Creating…' : 'Next'}}</button>
                    </div>
                </form>
            </mat-step>

            <!-- 3. details for AI -->
            <mat-step label="Tell AI about it" optional>
                <div class="step">
                    <p class="intro">The more you tell AI, the closer the model will be to how you really work. Everything here is optional: leave anything out and AI will use what's typical for your industry.</p>

                    @for (q of questions; track q.key) {
                        <mat-form-field appearance="fill">
                            <mat-label>{{q.label}}</mat-label>
                            <mat-chip-grid #grid [attr.aria-label]="q.label">
                                @for (item of answers[q.key]; track item) {
                                    <mat-chip-row (removed)="removeAnswer(q.key, item)">
                                        {{item}}
                                        <button matChipRemove [attr.aria-label]="'Remove ' + item"><mat-icon>cancel</mat-icon></button>
                                    </mat-chip-row>
                                }
                                <input [matChipInputFor]="grid" [matChipInputSeparatorKeyCodes]="separatorKeys" [matChipInputAddOnBlur]="true" (matChipInputTokenEnd)="addAnswer(q.key, $event)">
                            </mat-chip-grid>
                            <mat-hint>{{q.hint}}. Press Enter after each one.</mat-hint>
                        </mat-form-field>
                    }

                    <mat-form-field appearance="fill">
                        <mat-label>Anything else AI should know?</mat-label>
                        <textarea matInput [formControl]="more" cdkTextareaAutosize cdkAutosizeMinRows="3" placeholder="e.g. Orders over $5,000 need a manager's approval. Returns go back to the warehouse."></textarea>
                    </mat-form-field>

                    <div class="upload">
                        <input type="file" #fileInput hidden [accept]="AI_FILE_ACCEPT" (change)="onFileSelected($event)">
                        @if (file) {
                            <mat-chip (removed)="removeFile()">
                                <mat-icon matChipAvatar>description</mat-icon>
                                {{file.name}}
                                <button matChipRemove aria-label="Remove file"><mat-icon>cancel</mat-icon></button>
                            </mat-chip>
                        }@else {
                            <button mat-stroked-button type="button" (click)="fileInput.click()"><mat-icon>upload_file</mat-icon>Add a document or diagram</button>
                        }
                        <span class="hint">Have a procedure document, process map or photo of a whiteboard? AI can read it. PDF, image, text or XPDL, up to 2MB.</span>
                    </div>
                    @if (fileError) {
                        <p class="error" role="alert">{{fileError}}</p>
                    }

                    <div class="actions">
                        <button mat-flat-button color="accent" type="button" (click)="describe()" [disabled]="describing">Describe my process</button>
                        <button mat-button type="button" (click)="startBlank()">Start with a blank model instead</button>
                    </div>
                </div>
            </mat-step>

            <!-- 4. check and build -->
            <mat-step label="Check and build">
                <div class="step">
                    @if (describing) {
                        <div class="working" role="status">
                            <p><b>AI is describing your process.</b> This usually takes 30 to 60 seconds.</p>
                            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                            @if (slow) {
                                <p class="hint">Still working: detailed processes and documents take longer.</p>
                            }
                        </div>
                    }@else if (building) {
                        <div class="working" role="status">
                            <p><b>AI is building your model</b> from the description. This usually takes 30 to 60 seconds.</p>
                            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                            @if (slow) {
                                <p class="hint">Still working: larger processes take longer.</p>
                            }
                        </div>
                    }@else if (describeError && !description.value) {
                        <p class="error" role="alert">{{describeError}}</p>
                        <div class="actions">
                            <button mat-flat-button color="accent" type="button" (click)="describe()">Try again</button>
                            <button mat-button type="button" (click)="build(false)">Build without checking</button>
                            <button mat-button matStepperPrevious type="button">Back</button>
                        </div>
                    }@else {
                        <p class="intro">Here's how AI understands your process. Correct anything that's wrong, and add any steps that are missing. AI builds your model from this description.</p>
                        <mat-form-field appearance="fill" class="description">
                            <mat-label>Your process</mat-label>
                            <textarea matInput [formControl]="description" cdkTextareaAutosize cdkAutosizeMinRows="8" cdkAutosizeMaxRows="24" [maxlength]="AI_PROCESS_DESCRIPTION_MAX"></textarea>
                            @if (description.hasError('required')) {
                                <mat-error>AI needs a description to build from</mat-error>
                            }
                        </mat-form-field>

                        @if (describeError) {
                            <p class="error" role="alert">{{describeError}}</p>
                        }
                        @if (buildError) {
                            <p class="error" role="alert">{{buildError}}</p>
                        }
                        <div class="actions">
                            @if (buildTimedOut) {
                                <button mat-flat-button color="accent" type="button" (click)="openModel()">Open my model</button>
                            }@else {
                                <button mat-flat-button color="accent" type="button" (click)="build()" [disabled]="description.invalid">Build my model</button>
                            }
                            <button mat-button type="button" (click)="describe()">Describe again</button>
                            <button mat-button matStepperPrevious type="button">Change my answers</button>
                        </div>
                    }
                </div>
            </mat-step>
        </mat-stepper>
    }
</main>
